import React, { useState, useEffect } from 'react';

/**
 * TableCard — wraps the standard table layout with built-in client-side pagination.
 *
 * Usage (with built-in pagination):
 *   <TableCard rows={allRows} pageSize={10} ...>
 *     {(pageRows) => (
 *       <table>
 *         <thead>...</thead>
 *         <tbody>{pageRows.map(r => <tr key={r.id}>...</tr>)}</tbody>
 *       </table>
 *     )}
 *   </TableCard>
 *
 * Usage (manual / no pagination — pass children as ReactNode):
 *   <TableCard loading={loading} ...>
 *     <table>...</table>
 *   </TableCard>
 *
 * Props:
 *   label      string      — section label above the card
 *   toolbar    ReactNode   — toolbar row (buttons, selects, etc.)
 *   search     ReactNode   — search input slot
 *   loading    boolean
 *   empty      boolean     — show empty state (auto-computed when rows provided)
 *   rows       array       — full data array for built-in pagination
 *   pageSize   number      — rows per page (default 10), only used when rows provided
 *   children   fn|ReactNode — render(pageRows) or plain ReactNode
 */
export default function TableCard({
  label,
  toolbar,
  search,
  loading = false,
  empty,
  rows,
  pageSize = 10,
  children,
}) {
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the data changes (e.g. after filter)
  useEffect(() => { setPage(1); }, [rows]);

  const hasBuiltinPagination = Array.isArray(rows);
  const total = hasBuiltinPagination ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageRows = hasBuiltinPagination
    ? rows.slice((safePage - 1) * pageSize, safePage * pageSize)
    : null;

  const isEmptyState = hasBuiltinPagination
    ? (!loading && rows.length === 0)
    : (empty ?? false);

  const content = () => {
    if (loading) return <div className="table-loading">Chargement…</div>;
    if (isEmptyState) return <div className="table-empty">Aucun résultat</div>;
    if (hasBuiltinPagination && typeof children === 'function') return children(pageRows);
    return children;
  };

  const go = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);

  return (
    <>
      {label && <div className="section-label">{label}</div>}
      {toolbar && <div className="toolbar">{toolbar}</div>}
      {search && search}
      <div className="table-card">
        <div className="table-scroll">
          {content()}
        </div>

        {/* Built-in pagination — only shown when rows prop is provided */}
        {hasBuiltinPagination && !loading && total > pageSize && (
          <div className="pagination">
            <span className="pagination-info">
              {total > 0 ? `${start}–${end} / ${total}` : '0 résultat'}
            </span>
            <div className="pagination-buttons">
              <button className="page-btn" disabled={safePage <= 1} onClick={() => go(safePage - 1)}>&#8249;</button>
              {buildPageRange(safePage, totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`el-${i}`} style={{ padding: '0 4px', color: '#aaa', fontSize: 12 }}>…</span>
                ) : (
                  <button
                    key={p}
                    className={`page-btn${p === safePage ? ' active' : ''}`}
                    onClick={() => go(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button className="page-btn" disabled={safePage >= totalPages} onClick={() => go(safePage + 1)}>&#8250;</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * AntPagination — bridges server-side pagination (external state)
 * into the custom .page-btn style. Use when data is fetched per-page.
 */
export function AntPagination({ current, total, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pages = buildPageRange(current, totalPages);

  const go = (p) => {
    if (p >= 1 && p <= totalPages && p !== current) onChange(p, pageSize);
  };

  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <span className="pagination-info">{total > 0 ? `${start}–${end} / ${total}` : '0 résultat'}</span>
      {totalPages > 1 && (
        <div className="pagination-buttons">
          <button className="page-btn" disabled={current <= 1} onClick={() => go(current - 1)}>&#8249;</button>
          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#aaa', fontSize: 12 }}>…</span>
            ) : (
              <button
                key={p}
                className={`page-btn${p === current ? ' active' : ''}`}
                onClick={() => go(p)}
              >
                {p}
              </button>
            )
          )}
          <button className="page-btn" disabled={current >= totalPages} onClick={() => go(current + 1)}>&#8250;</button>
        </div>
      )}
    </div>
  );
}

function buildPageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}
